import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import {
  X, Zap, Loader2, Image as ImageIcon, Video as VideoIcon, Link2, Save, FolderOpen, Trash2,
  ChevronRight, ChevronLeft, Check, Plus,
} from "lucide-react";
import LandingUrlBuilder from "./LandingUrlBuilder";

// ─────────────────────────── Constants ───────────────────────────
const OBJECTIVES = [
  { value: "OUTCOME_TRAFFIC", label: "Traffic" },
  { value: "OUTCOME_ENGAGEMENT", label: "Engagement" },
  { value: "OUTCOME_AWARENESS", label: "Awareness" },
  { value: "OUTCOME_LEADS", label: "Leads" },
  { value: "OUTCOME_SALES", label: "Sales" },
  { value: "OUTCOME_APP_PROMOTION", label: "App Promotion" },
];
const BUYING_TYPES = [
  { value: "AUCTION", label: "Auction" },
  { value: "RESERVED", label: "Reservation" },
];
const BID_STRATEGIES = [
  { value: "LOWEST_COST_WITHOUT_CAP", label: "Lowest cost" },
  { value: "LOWEST_COST_WITH_BID_CAP", label: "Bid cap" },
  { value: "COST_CAP", label: "Cost cap" },
  { value: "LOWEST_COST_WITH_MIN_ROAS", label: "Minimum ROAS" },
];
const SPECIAL_AD_CATEGORIES = [
  { value: "CREDIT", label: "Credit / Financial services" },
  { value: "EMPLOYMENT", label: "Employment" },
  { value: "HOUSING", label: "Housing" },
  { value: "ISSUES_ELECTIONS_POLITICS", label: "Social issues, Elections, Politics" },
  { value: "ONLINE_GAMBLING_AND_GAMING", label: "Online gambling & gaming" },
];
const OPTIMIZATION_GOALS = [
  { value: "LINK_CLICKS", label: "Link clicks" },
  { value: "OFFSITE_CONVERSIONS", label: "Conversions (pixel)" },
  { value: "LEAD_GENERATION", label: "Lead generation" },
  { value: "IMPRESSIONS", label: "Impressions" },
  { value: "REACH", label: "Reach" },
  { value: "POST_ENGAGEMENT", label: "Post engagement" },
  { value: "LANDING_PAGE_VIEWS", label: "Landing page views" },
  { value: "VIDEO_VIEWS", label: "ThruPlay / Video views" },
];
const BILLING_EVENTS = [
  { value: "IMPRESSIONS", label: "Impressions" },
  { value: "LINK_CLICKS", label: "Link clicks" },
  { value: "THRUPLAY", label: "ThruPlay" },
];
const CTAS = ["LEARN_MORE", "SIGN_UP", "SHOP_NOW", "GET_OFFER", "SUBSCRIBE", "BOOK_TRAVEL", "DOWNLOAD", "APPLY_NOW", "CONTACT_US"];
const COUNTRIES = [
  { code: "US", label: "United States" }, { code: "CA", label: "Canada" },
  { code: "GB", label: "United Kingdom" }, { code: "AU", label: "Australia" },
  { code: "DE", label: "Germany" }, { code: "FR", label: "France" },
  { code: "IT", label: "Italy" }, { code: "ES", label: "Spain" },
  { code: "MX", label: "Mexico" }, { code: "BR", label: "Brazil" },
  { code: "IN", label: "India" }, { code: "JP", label: "Japan" },
];
const PLACEMENT_PLATFORMS = [
  { value: "facebook", label: "Facebook" },
  { value: "instagram", label: "Instagram" },
  { value: "audience_network", label: "Audience Network" },
  { value: "messenger", label: "Messenger" },
];

const STEPS = [
  { id: "campaign", label: "Campaign" },
  { id: "adset", label: "Ad Set" },
  { id: "ad", label: "Ad" },
  { id: "review", label: "Review" },
];

// ─────────────────────────── Main component ───────────────────────────
export default function LaunchCampaignModal({ open, onClose, adAccounts = [], defaultAdAccountId }) {
  const [form, setForm] = useState(() => defaultForm(adAccounts, defaultAdAccountId));
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState("");
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [showUrlBuilder, setShowUrlBuilder] = useState(false);
  const [busySave, setBusySave] = useState(false);

  // Async-loaded FB assets
  const [pixels, setPixels] = useState([]);
  const [pixelEvents, setPixelEvents] = useState([]);
  const [pages, setPages] = useState([]);
  const [igAccounts, setIgAccounts] = useState([]);
  const [customAudiences, setCustomAudiences] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState({});

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/campaigns/templates");
        setTemplates(data.templates || []);
      } catch (e) { console.error("templates load:", e); }
    })();
  }, [open]);

  // When modal opens: pre-seed ad account from filter, reset step.
  useEffect(() => {
    if (open && defaultAdAccountId && defaultAdAccountId !== "all") {
      setForm((f) => ({ ...f, ad_account_id: defaultAdAccountId }));
    }
    if (open) setStep(0);
  }, [open, defaultAdAccountId]);

  // Load pixels + custom audiences whenever ad_account changes
  useEffect(() => {
    if (!open || !form.ad_account_id) return;
    setLoadingAssets((s) => ({ ...s, pixels: true, audiences: true }));
    api.get(`/fb/pixels`, { params: { ad_account_id: form.ad_account_id } })
      .then(({ data }) => setPixels(data.pixels || []))
      .catch(() => setPixels([]))
      .finally(() => setLoadingAssets((s) => ({ ...s, pixels: false })));
    api.get(`/fb/custom-audiences`, { params: { ad_account_id: form.ad_account_id } })
      .then(({ data }) => setCustomAudiences(data.custom_audiences || []))
      .catch(() => setCustomAudiences([]))
      .finally(() => setLoadingAssets((s) => ({ ...s, audiences: false })));
  }, [open, form.ad_account_id]);

  // Load FB pages once when modal opens
  useEffect(() => {
    if (!open) return;
    setLoadingAssets((s) => ({ ...s, pages: true }));
    api.get(`/fb/pages`)
      .then(({ data }) => setPages(data.pages || []))
      .catch(() => setPages([]))
      .finally(() => setLoadingAssets((s) => ({ ...s, pages: false })));
  }, [open]);

  // Load IG accounts when page changes
  useEffect(() => {
    if (!form.page_id) return setIgAccounts([]);
    setLoadingAssets((s) => ({ ...s, ig: true }));
    api.get(`/fb/instagram-accounts`, { params: { page_id: form.page_id } })
      .then(({ data }) => setIgAccounts(data.instagram_accounts || []))
      .catch(() => setIgAccounts([]))
      .finally(() => setLoadingAssets((s) => ({ ...s, ig: false })));
  }, [form.page_id]);

  // Load pixel events when pixel changes
  useEffect(() => {
    if (!form.pixel_id) return setPixelEvents([]);
    setLoadingAssets((s) => ({ ...s, events: true }));
    api.get(`/fb/pixel-events`, { params: { pixel_id: form.pixel_id } })
      .then(({ data }) => setPixelEvents(data.events || []))
      .catch(() => setPixelEvents([]))
      .finally(() => setLoadingAssets((s) => ({ ...s, events: false })));
  }, [form.pixel_id]);

  // ─── Validators per step (hooks must run unconditionally; keep before early-return) ───
  const stepErrors = useMemo(() => validateForm(form), [form]);
  // ── Compute spin count (0 = not in spin mode, ≥2 = spin) ──
  const spinCount = useMemo(() => {
    const cp = (form.creative_pool || []).filter((c) => c && (c.image_url || c.video_url));
    const kp = (form.copy_pool || []).filter((k) => k && k.headline);
    if (cp.length === 0 && kp.length === 0) return 0;
    if (form.pairing_mode === "cross") return Math.min(10, Math.max(1, cp.length) * Math.max(1, kp.length));
    return Math.min(10, Math.max(cp.length, kp.length, 1));
  }, [form.creative_pool, form.copy_pool, form.pairing_mode]);

  if (!open) return null;

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggleInList = (key, val) =>
    set({ [key]: form[key].includes(val) ? form[key].filter((x) => x !== val) : [...form[key], val] });

  // ─── Template handlers ───
  const loadTemplate = (tpl) => { setForm((f) => ({ ...f, ...tpl.form, spin_template_id: tpl.template_id })); toast.success(`Loaded "${tpl.name}"`); };
  const saveTemplate = async () => {
    const name = templateName.trim();
    if (!name) return toast.error("Template name required");
    setBusySave(true);
    try {
      const { data } = await api.post("/campaigns/templates", { name, form });
      toast.success(data.overwritten ? `"${name}" updated` : `Saved as "${name}"`);
      const { data: listed } = await api.get("/campaigns/templates");
      setTemplates(listed.templates || []);
      setShowSaveTemplate(false);
      setTemplateName("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusySave(false); }
  };
  const deleteTemplate = async (tpl) => {
    if (!window.confirm(`Delete "${tpl.name}"?`)) return;
    try {
      await api.delete(`/campaigns/templates/${tpl.template_id}`);
      setTemplates((t) => t.filter((x) => x.template_id !== tpl.template_id));
      toast.success(`Deleted`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  // ─── Validators per step ───
  const currentStepValid = STEPS[step].id === "review" ? stepErrors.length === 0 : stepErrors.filter((e) => e.step === STEPS[step].id).length === 0;

  // ── Compute spin count (0 = not in spin mode, ≥2 = spin) ──

  const launch = async () => {
    const errs = validateForm(form);
    if (errs.length > 0) { toast.error(errs[0].msg); setStep(STEPS.findIndex((s) => s.id === errs[0].step)); return; }
    setBusy(true);
    try {
      // 1. Persist a lightweight "brief" doc so /ai/push-to-fb can reference it.
      const { data: b } = await api.post("/ai/briefs/manual", {
        title: form.campaign_name.trim(),
        description: form.primary_text.trim(),
        ad_copy: [{
          headline: form.headline.trim(),
          primary_text: form.primary_text.trim(),
          description: form.description.trim(),
        }],
      });

      // Shared payload for both single + spin endpoints.
      const basePayload = {
        brief_id: b.brief_id,
        ad_account_id: form.ad_account_id,
        campaign_name: form.campaign_name.trim(),
        page_id: form.page_id.trim(),
        daily_budget_cents: form.budget_kind === "daily" ? Math.round(parseFloat(form.budget) * 100) : 2000,
        lifetime_budget_cents: form.budget_kind === "lifetime" ? Math.round(parseFloat(form.budget) * 100) : null,
        image_urls: form.creative_format === "carousel"
          ? form.carousel_images.filter(Boolean)
          : (form.creative_kind === "image" && form.image_url ? [form.image_url.trim()] : []),
        video_url: form.creative_kind === "video" ? form.video_url.trim() : undefined,
        landing_url: form.landing_url.trim(),
        objective: form.objective,
        call_to_action: form.call_to_action,
        buying_type: form.buying_type,
        budget_strategy: form.budget_strategy,
        bid_strategy: form.bid_strategy,
        bid_amount_cents: form.bid_amount ? Math.round(parseFloat(form.bid_amount) * 100) : null,
        special_ad_categories: form.special_ad_categories,
        special_ad_category_country: form.special_ad_categories.length > 0 ? (form.special_ad_category_country || form.countries[0] || "US") : null,
        ab_test_enabled: form.ab_test_enabled,
        adset_name: form.adset_name.trim() || undefined,
        ad_name: form.ad_name.trim() || undefined,
        optimization_goal: form.optimization_goal,
        billing_event: form.billing_event,
        pixel_id: form.pixel_id || null,
        custom_event_type: form.custom_event_type || null,
        attribution_click_days: parseInt(form.attribution_click_days, 10) || 7,
        attribution_view_days: parseInt(form.attribution_view_days, 10) || 0,
        start_time: form.start_time || null,
        end_time: form.end_time || null,
        countries: form.countries,
        age_min: parseInt(form.age_min, 10) || 18,
        age_max: parseInt(form.age_max, 10) || 65,
        genders: form.genders,
        custom_audiences: form.custom_audiences,
        excluded_custom_audiences: form.excluded_custom_audiences,
        advantage_audience: form.advantage_audience,
        placements_auto: form.placements_auto,
        publisher_platforms: form.publisher_platforms,
        instagram_actor_id: form.instagram_actor_id || null,
        creative_format: form.creative_format,
        display_link: form.display_link || null,
        url_tags: form.url_tags || null,
        multi_advertiser_ads: form.multi_advertiser_ads,
        advantage_creative_enhancements: form.advantage_creative_enhancements,
      };

      if (spinCount >= 2) {
        // Spin N ads flow: single campaign + adset with N variant ads.
        // Strip UI-only `_id` before hitting the backend.
        const strip = (o) => { const { _id, ...rest } = o; void _id; return rest; };
        const { data } = await api.post("/ai/push-to-fb/spin", {
          ...basePayload,
          template_id: form.spin_template_id,
          creative_pool: (form.creative_pool || []).map(strip),
          copy_pool: (form.copy_pool || []).map(strip),
          pairing_mode: form.pairing_mode,
          max_variants: spinCount,
        });
        if (data.success) {
          toast.success(`Spun ${data.variants_created}/${data.variants_requested} ads in campaign ${data.campaign_id} (paused)`);
          if (data.note) toast.info(data.note);
          onClose?.(true);
        } else {
          toast.error(data.error || "Spin failed");
        }
        return;
      }

      // Single-launch flow.
      const { data } = await api.post("/ai/push-to-fb", basePayload);

      if (data.success) {
        toast.success(`Campaign ${data.campaign_id} created (paused)`);
        if (data.note) toast.info(data.note);
        if (data.ab_test_pending) toast.info("A/B test flag set — configure the experiment in FB Ads Manager before publishing.");
        onClose?.(true);
      } else {
        toast.error(data.error || "Launch failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Launch failed");
    } finally { setBusy(false); }
  };

  // ─── Render ───
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => onClose?.()}>
      <div
        className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="launch-campaign-modal"
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-600 rounded text-white flex items-center justify-center">
              <Zap className="w-4 h-4" />
            </div>
            <div>
              <div className="text-sm font-semibold">Launch campaign manually</div>
              <div className="text-[11px] text-slate-500 font-mono">FB wizard · campaign → adset → ad, all PAUSED</div>
            </div>
          </div>
          <button onClick={() => onClose?.()} data-testid="launch-close-btn" className="text-slate-400 hover:text-slate-900">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Stepper */}
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-1 shrink-0">
          {STEPS.map((s, i) => {
            const done = i < step;
            const active = i === step;
            return (
              <button
                key={s.id}
                onClick={() => setStep(i)}
                data-testid={`wizard-step-${s.id}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                  active ? "bg-blue-600 text-white" : done ? "text-blue-700 hover:bg-blue-50" : "text-slate-500 hover:bg-slate-50"
                }`}
              >
                <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] ${
                  active ? "bg-white text-blue-600" : done ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-600"
                }`}>
                  {done ? <Check className="w-2.5 h-2.5" /> : i + 1}
                </span>
                {s.label}
                {i < STEPS.length - 1 && <ChevronRight className="w-3 h-3 text-slate-300 ml-0.5" />}
              </button>
            );
          })}
        </div>

        {/* Body */}
        <div className="overflow-y-auto p-5 space-y-4 flex-1">
          {/* Templates always visible */}
          <TemplatesBar
            templates={templates} showSaveTemplate={showSaveTemplate}
            setShowSaveTemplate={setShowSaveTemplate}
            templateName={templateName} setTemplateName={setTemplateName}
            busySave={busySave} saveTemplate={saveTemplate}
            loadTemplate={loadTemplate} deleteTemplate={deleteTemplate}
          />

          {STEPS[step].id === "campaign" && (
            <CampaignStep form={form} set={set} toggleInList={toggleInList} adAccounts={adAccounts} />
          )}
          {STEPS[step].id === "adset" && (
            <AdSetStep
              form={form} set={set} toggleInList={toggleInList}
              pixels={pixels} pixelEvents={pixelEvents}
              customAudiences={customAudiences}
              loadingAssets={loadingAssets}
            />
          )}
          {STEPS[step].id === "ad" && (
            <AdStep
              form={form} set={set}
              pages={pages} igAccounts={igAccounts}
              loadingAssets={loadingAssets}
              openUrlBuilder={() => setShowUrlBuilder(true)}
            />
          )}
          {STEPS[step].id === "review" && <ReviewStep form={form} stepErrors={stepErrors} onJumpTo={(s) => setStep(STEPS.findIndex((x) => x.id === s))} />}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-5 py-3 flex items-center justify-between shrink-0">
          <div className="text-[11px] text-slate-500 font-mono">
            All entities will be created in <strong className="text-amber-600">PAUSED</strong> status.
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => onClose?.()} className="text-xs font-medium text-slate-500 hover:text-slate-900 px-3 py-2">Cancel</button>
            {step > 0 && (
              <button
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                data-testid="wizard-back-btn"
                className="text-xs font-medium px-3 py-2 border border-slate-200 rounded hover:bg-slate-50 flex items-center gap-1"
              >
                <ChevronLeft className="w-3 h-3" /> Back
              </button>
            )}
            {step < STEPS.length - 1 ? (
              <button
                onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
                disabled={!currentStepValid}
                data-testid="wizard-next-btn"
                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-medium rounded-md px-4 py-2 flex items-center gap-1.5"
              >
                Next <ChevronRight className="w-3 h-3" />
              </button>
            ) : (
              <button
                onClick={launch}
                disabled={busy || stepErrors.length > 0}
                data-testid="launch-submit-btn"
                className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md px-4 py-2 flex items-center gap-1.5 disabled:opacity-60 transition-colors"
              >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
                {busy ? "Launching…" : spinCount >= 2 ? `Spin ${spinCount} ads (paused)` : "Publish (paused)"}
              </button>
            )}
          </div>
        </div>
      </div>

      <LandingUrlBuilder
        open={showUrlBuilder}
        onClose={() => setShowUrlBuilder(false)}
        onUse={(url) => setForm((f) => ({ ...f, landing_url: url }))}
      />
    </div>
  );
}

// ─────────────────────────── Default form ───────────────────────────
function defaultForm(adAccounts, defaultAdAccountId) {
  return {
    // Campaign
    campaign_name: "",
    objective: "OUTCOME_TRAFFIC",
    buying_type: "AUCTION",
    budget_strategy: "adset",
    budget_kind: "daily",
    budget: "20",
    bid_strategy: "LOWEST_COST_WITHOUT_CAP",
    bid_amount: "",
    special_ad_categories: [],
    special_ad_category_country: "",
    ab_test_enabled: false,
    ad_account_id: defaultAdAccountId && defaultAdAccountId !== "all" ? defaultAdAccountId : (adAccounts[0]?.id || ""),

    // Ad set
    adset_name: "",
    optimization_goal: "LINK_CLICKS",
    billing_event: "IMPRESSIONS",
    pixel_id: "",
    custom_event_type: "",
    attribution_click_days: "7",
    attribution_view_days: "1",
    start_time: "",
    end_time: "",
    countries: ["US"],
    age_min: "25",
    age_max: "65",
    genders: [],
    custom_audiences: [],
    excluded_custom_audiences: [],
    advantage_audience: true,
    placements_auto: true,
    publisher_platforms: ["facebook", "instagram", "audience_network", "messenger"],

    // Ad
    ad_name: "",
    page_id: "",
    instagram_actor_id: "",
    creative_kind: "image",
    creative_format: "single",
    image_url: "",
    video_url: "",
    carousel_images: ["", ""],
    landing_url: "",
    display_link: "",
    url_tags: "",
    headline: "",
    primary_text: "",
    description: "",
    call_to_action: "LEARN_MORE",
    multi_advertiser_ads: false,
    advantage_creative_enhancements: false,

    // Variant pool — when non-empty, Publish becomes Spin N ads
    creative_pool: [],  // [{image_url?, video_url?}, …]
    copy_pool: [],      // [{headline, primary_text, description}, …]
    pairing_mode: "zip", // "zip" | "cross"
    spin_template_id: null,
  };
}

// ─────────────────────────── Validation ───────────────────────────
function validateForm(f) {
  const errs = [];
  if (!f.campaign_name.trim()) errs.push({ step: "campaign", msg: "Campaign name required" });
  if (!f.ad_account_id) errs.push({ step: "campaign", msg: "Ad account required" });
  if (parseFloat(f.budget) < 1) errs.push({ step: "campaign", msg: "Budget must be ≥ $1" });
  if (f.special_ad_categories.length > 0 && !(f.special_ad_category_country || f.countries[0])) {
    errs.push({ step: "campaign", msg: "Special ad category needs a country" });
  }

  if (f.countries.length === 0) errs.push({ step: "adset", msg: "Pick at least one country" });
  if (parseInt(f.age_min) > parseInt(f.age_max)) errs.push({ step: "adset", msg: "Age min > age max" });
  if (f.optimization_goal === "OFFSITE_CONVERSIONS" && !f.pixel_id) errs.push({ step: "adset", msg: "Conversion optimization requires a pixel" });
  if (f.optimization_goal === "OFFSITE_CONVERSIONS" && !f.custom_event_type) errs.push({ step: "adset", msg: "Pick a conversion event" });

  if (!f.page_id.trim()) errs.push({ step: "ad", msg: "FB Page required" });
  if (!f.landing_url.trim() || !f.landing_url.startsWith("http")) errs.push({ step: "ad", msg: "Valid landing URL required" });
  if (!f.headline.trim()) errs.push({ step: "ad", msg: "Headline required" });
  if (!f.primary_text.trim()) errs.push({ step: "ad", msg: "Primary text required" });
  if (f.creative_format === "single") {
    if (f.creative_kind === "image" && !f.image_url.trim()) errs.push({ step: "ad", msg: "Image URL required" });
    if (f.creative_kind === "video" && !f.video_url.trim()) errs.push({ step: "ad", msg: "Video URL required" });
  }
  if (f.creative_format === "carousel") {
    const filled = f.carousel_images.filter(Boolean).length;
    if (filled < 2) errs.push({ step: "ad", msg: "Carousel needs at least 2 images" });
  }
  return errs;
}

// ─────────────────────────── Reusable bits ───────────────────────────
const L = ({ children, hint }) => (
  <label className="text-xs font-medium text-slate-700 block mb-1">
    {children}
    {hint && <span className="text-[10px] text-slate-400 font-mono ml-1">· {hint}</span>}
  </label>
);

const Toggle = ({ checked, onChange, testId }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    data-testid={testId}
    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${checked ? "bg-blue-600" : "bg-slate-300"}`}
  >
    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${checked ? "translate-x-5" : "translate-x-1"}`} />
  </button>
);

const Input = (props) => (
  <input
    {...props}
    className={`w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500/40 ${props.className || ""}`}
  />
);
const Select = ({ children, ...props }) => (
  <select
    {...props}
    className={`w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500/40 ${props.className || ""}`}
  >{children}</select>
);

// ─────────────────────────── Templates bar ───────────────────────────
function TemplatesBar({ templates, showSaveTemplate, setShowSaveTemplate, templateName, setTemplateName, busySave, saveTemplate, loadTemplate, deleteTemplate }) {
  return (
    <section className="bg-slate-50 border border-slate-200 rounded-md p-3 space-y-2" data-testid="templates-panel">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-slate-900 flex items-center gap-1.5">
          <FolderOpen className="w-3 h-3" /> Templates
        </div>
        <button
          onClick={() => setShowSaveTemplate((v) => !v)} data-testid="toggle-save-template"
          className="text-[11px] font-medium px-2 py-1 bg-white border border-slate-200 rounded hover:bg-slate-100 flex items-center gap-1"
        >
          <Save className="w-3 h-3" /> Save current as template
        </button>
      </div>
      {showSaveTemplate && (
        <div className="flex items-center gap-2 pt-1">
          <Input value={templateName} onChange={(e) => setTemplateName(e.target.value)}
            placeholder='e.g. "US · Debt Relief · CBO $20/day · Sora"' data-testid="template-name-input" className="flex-1 font-mono" />
          <button onClick={saveTemplate} disabled={busySave} data-testid="save-template-btn"
            className="text-[11px] font-medium px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-60 flex items-center gap-1">
            {busySave && <Loader2 className="w-3 h-3 animate-spin" />} Save
          </button>
        </div>
      )}
      {templates.length > 0 ? (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {templates.map((tpl) => (
            <div key={tpl.template_id} className="group flex items-center gap-0.5 bg-white border border-slate-200 hover:border-blue-300 rounded pr-1">
              <button onClick={() => loadTemplate(tpl)} data-testid={`template-load-${tpl.template_id}`}
                className="text-[11px] px-2 py-1 rounded hover:bg-blue-50 text-slate-700 flex items-center gap-1.5"
                title={`by ${tpl.created_by_name || "?"}${tpl.last_used_by_name ? ` · last used by ${tpl.last_used_by_name}` : ""}`}
              >
                <span className="font-medium">{tpl.name}</span>
                {tpl.times_used > 0 && (
                  <span className="text-[9px] font-mono bg-green-100 text-green-800 rounded-full px-1.5 py-0.5" data-testid={`template-usage-${tpl.template_id}`}>
                    ×{tpl.times_used}
                  </span>
                )}
                {tpl.created_by_name && (
                  <span className="text-[9px] text-slate-400 font-mono hidden md:inline">@{tpl.created_by_name.split(" ")[0]?.toLowerCase() || tpl.created_by_name}</span>
                )}
              </button>
              <button onClick={() => deleteTemplate(tpl)} data-testid={`template-delete-${tpl.template_id}`}
                className="text-slate-300 hover:text-red-600 p-1 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-[11px] text-slate-400 font-mono italic">No templates yet — save one from this form, your whole team will see it.</div>
      )}
    </section>
  );
}

// ─────────────────────────── Step 1: Campaign ───────────────────────────
function CampaignStep({ form, set, toggleInList, adAccounts }) {
  const specialCategoryCountry = form.special_ad_category_country || form.countries[0] || "US";
  return (
    <div className="space-y-4" data-testid="step-campaign">
      <Card title="Campaign name">
        <Input value={form.campaign_name} onChange={(e) => set({ campaign_name: e.target.value })}
          placeholder="e.g. US_RJ_Drapes Are Out_002" data-testid="campaign-name-input" />
      </Card>

      <Card title="Campaign details">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <L>Buying type</L>
            <Select value={form.buying_type} onChange={(e) => set({ buying_type: e.target.value })} data-testid="buying-type-select">
              {BUYING_TYPES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
            </Select>
          </div>
          <div>
            <L>Campaign objective</L>
            <Select value={form.objective} onChange={(e) => set({ objective: e.target.value })} data-testid="campaign-objective-select">
              {OBJECTIVES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
          </div>
          <div className="col-span-2">
            <L>Ad account</L>
            <Select value={form.ad_account_id} onChange={(e) => set({ ad_account_id: e.target.value })} data-testid="ad-account-select">
              {adAccounts.length === 0 && <option value="">(no FB ad accounts connected)</option>}
              {adAccounts.map((a) => <option key={a.id} value={a.id}>{a.name || a.id}</option>)}
            </Select>
          </div>
        </div>
      </Card>

      <Card title="Budget">
        <div className="space-y-3">
          <RadioRow
            label="Budget strategy"
            value={form.budget_strategy}
            onChange={(v) => set({ budget_strategy: v })}
            options={[
              { value: "campaign", label: "Campaign budget (CBO)", hint: "Auto-distribute across adsets" },
              { value: "adset", label: "Ad set budget (ABO)", hint: "Set budget per adset" },
            ]}
            testId="budget-strategy"
          />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <L>Budget type</L>
              <Select value={form.budget_kind} onChange={(e) => set({ budget_kind: e.target.value })} data-testid="budget-kind-select">
                <option value="daily">Daily budget</option>
                <option value="lifetime">Lifetime budget</option>
              </Select>
            </div>
            <div>
              <L>{form.budget_kind === "daily" ? "Daily" : "Lifetime"} budget ($)</L>
              <Input type="number" min="1" step="1" value={form.budget} onChange={(e) => set({ budget: e.target.value })} data-testid="budget-amount-input" className="font-mono" />
            </div>
            <div>
              <L>Bid strategy</L>
              <Select value={form.bid_strategy} onChange={(e) => set({ bid_strategy: e.target.value })} data-testid="bid-strategy-select">
                {BID_STRATEGIES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
              </Select>
            </div>
            {(form.bid_strategy === "LOWEST_COST_WITH_BID_CAP" || form.bid_strategy === "COST_CAP") && (
              <div>
                <L>{form.bid_strategy === "COST_CAP" ? "Cost cap" : "Bid cap"} ($)</L>
                <Input type="number" min="0.01" step="0.01" value={form.bid_amount} onChange={(e) => set({ bid_amount: e.target.value })} data-testid="bid-amount-input" className="font-mono" />
              </div>
            )}
          </div>
        </div>
      </Card>

      <Card title="A/B test">
        <div className="flex items-center justify-between">
          <div className="text-xs text-slate-600">Compare different versions to find winning creative.</div>
          <Toggle checked={form.ab_test_enabled} onChange={(v) => set({ ab_test_enabled: v })} testId="ab-test-toggle" />
        </div>
        {form.ab_test_enabled && (
          <div className="mt-2 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 font-mono">
            ⚠ A/B experiment must be finalized in FB Ads Manager after publishing.
          </div>
        )}
      </Card>

      <Card title="Special Ad Categories" hint="Required for credit, employment, housing, political ads">
        <div className="flex flex-wrap gap-1.5 mb-2">
          {SPECIAL_AD_CATEGORIES.map((c) => {
            const on = form.special_ad_categories.includes(c.value);
            return (
              <button key={c.value} onClick={() => toggleInList("special_ad_categories", c.value)} data-testid={`special-cat-${c.value}`}
                className={`text-[11px] px-2 py-1 rounded border transition-colors ${on ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                {c.label}
              </button>
            );
          })}
        </div>
        {form.special_ad_categories.length > 0 && (
          <div>
            <L>Country (for category declaration)</L>
            <Select value={specialCategoryCountry} onChange={(e) => set({ special_ad_category_country: e.target.value })} data-testid="special-cat-country">
              {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
            </Select>
          </div>
        )}
      </Card>
    </div>
  );
}

// ─────────────────────────── Step 2: Ad Set ───────────────────────────
function AdSetStep({ form, set, toggleInList, pixels, pixelEvents, customAudiences, loadingAssets }) {
  const needsConversion = form.optimization_goal === "OFFSITE_CONVERSIONS";
  return (
    <div className="space-y-4" data-testid="step-adset">
      <Card title="Ad set name">
        <Input value={form.adset_name} onChange={(e) => set({ adset_name: e.target.value })}
          placeholder={`${form.campaign_name || "(Campaign)"} - AdSet`} data-testid="adset-name-input" />
      </Card>

      <Card title="Conversion">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <L>Performance goal</L>
            <Select value={form.optimization_goal} onChange={(e) => set({ optimization_goal: e.target.value })} data-testid="optimization-goal-select">
              {OPTIMIZATION_GOALS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
          </div>
          <div>
            <L>Billing event</L>
            <Select value={form.billing_event} onChange={(e) => set({ billing_event: e.target.value })} data-testid="billing-event-select">
              {BILLING_EVENTS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
            </Select>
          </div>
          <div>
            <L>Dataset (Pixel) {loadingAssets.pixels && <Loader2 className="inline w-3 h-3 animate-spin" />}</L>
            <Select value={form.pixel_id} onChange={(e) => set({ pixel_id: e.target.value, custom_event_type: "" })} data-testid="pixel-select">
              <option value="">— None —</option>
              {pixels.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.id})</option>)}
            </Select>
          </div>
          <div>
            <L>Conversion event {needsConversion && <span className="text-red-500">*</span>}</L>
            <Select value={form.custom_event_type} onChange={(e) => set({ custom_event_type: e.target.value })} disabled={!form.pixel_id} data-testid="custom-event-select">
              <option value="">— Select —</option>
              {pixelEvents.map((ev) => <option key={ev.name} value={ev.name}>{ev.name}{ev.active ? " · 🟢 active" : ""}</option>)}
            </Select>
          </div>
          <div>
            <L>Attribution · click window (days)</L>
            <Select value={form.attribution_click_days} onChange={(e) => set({ attribution_click_days: e.target.value })} data-testid="attr-click-select">
              <option value="1">1-day click</option>
              <option value="7">7-day click</option>
            </Select>
          </div>
          <div>
            <L>Attribution · view window (days)</L>
            <Select value={form.attribution_view_days} onChange={(e) => set({ attribution_view_days: e.target.value })} data-testid="attr-view-select">
              <option value="0">No view</option>
              <option value="1">1-day view</option>
            </Select>
          </div>
        </div>
      </Card>

      <Card title="Schedule">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <L>Start time</L>
            <Input type="datetime-local" value={form.start_time} onChange={(e) => set({ start_time: e.target.value })} data-testid="start-time-input" />
          </div>
          <div>
            <L>End time (optional)</L>
            <Input type="datetime-local" value={form.end_time} onChange={(e) => set({ end_time: e.target.value })} data-testid="end-time-input" />
          </div>
        </div>
      </Card>

      <Card title="Audience">
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <L>Countries</L>
            <div className="flex flex-wrap gap-1.5" data-testid="countries-picker">
              {COUNTRIES.map((c) => {
                const on = form.countries.includes(c.code);
                return (
                  <button key={c.code} onClick={() => toggleInList("countries", c.code)} data-testid={`country-${c.code}`}
                    className={`text-[11px] px-2 py-1 rounded border transition-colors ${on ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                    {c.code} · {c.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div><L>Age min</L><Input type="number" min="13" max="65" value={form.age_min} onChange={(e) => set({ age_min: e.target.value })} data-testid="age-min-input" className="font-mono" /></div>
          <div><L>Age max</L><Input type="number" min="13" max="65" value={form.age_max} onChange={(e) => set({ age_max: e.target.value })} data-testid="age-max-input" className="font-mono" /></div>
          <div>
            <L>Gender</L>
            <div className="flex gap-1.5">
              {[{v:null,label:"All"},{v:1,label:"Male"},{v:2,label:"Female"}].map((g) => {
                const on = g.v === null ? form.genders.length === 0 : form.genders.includes(g.v);
                return (
                  <button key={g.label} data-testid={`gender-${g.label.toLowerCase()}`}
                    onClick={() => set({ genders: g.v === null ? [] : form.genders.includes(g.v) ? form.genders.filter((x) => x !== g.v) : [...form.genders, g.v] })}
                    className={`flex-1 text-[11px] px-2 py-1 rounded border ${on ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"}`}>
                    {g.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex items-center justify-between col-span-2 pt-2 border-t border-slate-100">
            <div className="text-xs">
              <div className="font-medium text-slate-900">Advantage+ audience</div>
              <div className="text-[11px] text-slate-500">FB broadens beyond your settings when performance is improved</div>
            </div>
            <Toggle checked={form.advantage_audience} onChange={(v) => set({ advantage_audience: v })} testId="advantage-audience-toggle" />
          </div>
          {customAudiences.length > 0 && (
            <div className="col-span-2">
              <L>Saved / Custom audiences {loadingAssets.audiences && <Loader2 className="inline w-3 h-3 animate-spin" />}</L>
              <div className="max-h-32 overflow-y-auto border border-slate-200 rounded p-2 space-y-1">
                {customAudiences.map((a) => (
                  <label key={a.id} className="flex items-center gap-2 text-xs cursor-pointer" data-testid={`audience-row-${a.id}`}>
                    <input type="checkbox" checked={form.custom_audiences.includes(a.id)}
                      onChange={() => toggleInList("custom_audiences", a.id)} />
                    <span className="flex-1">{a.name}</span>
                    <span className="text-[10px] text-slate-400 font-mono">{a.size_low?.toLocaleString() || "?"}-{a.size_high?.toLocaleString() || "?"}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      </Card>

      <Card title="Placements">
        <RadioRow
          value={form.placements_auto ? "auto" : "manual"}
          onChange={(v) => set({ placements_auto: v === "auto" })}
          options={[
            { value: "auto", label: "Advantage+ placements", hint: "Let FB pick where to show your ads (recommended)" },
            { value: "manual", label: "Manual placements", hint: "Choose specific FB/IG/Audience Network/Messenger positions" },
          ]}
          testId="placements-radio"
        />
        {!form.placements_auto && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {PLACEMENT_PLATFORMS.map((p) => {
              const on = form.publisher_platforms.includes(p.value);
              return (
                <button key={p.value} onClick={() => toggleInList("publisher_platforms", p.value)} data-testid={`platform-${p.value}`}
                  className={`text-[11px] px-2 py-1 rounded border ${on ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"}`}>
                  {p.label}
                </button>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

// ─────────────────────────── Step 3: Ad ───────────────────────────
function AdStep({ form, set, pages, igAccounts, loadingAssets, openUrlBuilder }) {
  return (
    <div className="space-y-4" data-testid="step-ad">
      <Card title="Ad name">
        <Input value={form.ad_name} onChange={(e) => set({ ad_name: e.target.value })}
          placeholder={`${form.campaign_name || "(Campaign)"} - Ad`} data-testid="ad-name-input" />
      </Card>

      <Card title="Identity">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <L>Facebook Page {loadingAssets.pages && <Loader2 className="inline w-3 h-3 animate-spin" />}</L>
            {pages.length > 0 ? (
              <Select value={form.page_id} onChange={(e) => set({ page_id: e.target.value, instagram_actor_id: "" })} data-testid="page-select">
                <option value="">— Select —</option>
                {pages.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.id})</option>)}
              </Select>
            ) : (
              <Input value={form.page_id} onChange={(e) => set({ page_id: e.target.value })} placeholder="123456789012345" data-testid="page-id-input" className="font-mono" />
            )}
          </div>
          <div>
            <L>Instagram account (optional) {loadingAssets.ig && <Loader2 className="inline w-3 h-3 animate-spin" />}</L>
            <Select value={form.instagram_actor_id} onChange={(e) => set({ instagram_actor_id: e.target.value })} data-testid="ig-account-select">
              <option value="">— Use Facebook Page —</option>
              {igAccounts.map((ig) => <option key={ig.id} value={ig.id}>{ig.name}</option>)}
            </Select>
          </div>
        </div>
      </Card>

      <Card title="Ad setup">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <L>Creative source</L>
            <div className="text-[11px] text-slate-500 px-2 py-2 bg-slate-50 rounded">Manual upload</div>
          </div>
          <div>
            <L>Format</L>
            <Select value={form.creative_format} onChange={(e) => set({ creative_format: e.target.value })} data-testid="format-select">
              <option value="single">Single image or video</option>
              <option value="carousel">Carousel</option>
            </Select>
          </div>
        </div>

        {form.creative_format === "single" && (
          <div className="mt-3">
            <div className="flex items-center gap-2 mb-3">
              {["image", "video"].map((k) => (
                <button key={k} onClick={() => set({ creative_kind: k })} data-testid={`creative-kind-${k}`}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-medium capitalize ${
                    form.creative_kind === k ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                  }`}>
                  {k === "image" ? <ImageIcon className="w-3 h-3" /> : <VideoIcon className="w-3 h-3" />} {k}
                </button>
              ))}
            </div>
            {form.creative_kind === "image" ? (
              <>
                <L hint="full HTTPS URL or /api/static/generated/…">Image URL</L>
                <Input value={form.image_url} onChange={(e) => set({ image_url: e.target.value })} data-testid="image-url-input" className="font-mono" />
              </>
            ) : (
              <>
                <L hint="paste from Video tiers or /api/static/videos/…">Video URL</L>
                <Input value={form.video_url} onChange={(e) => set({ video_url: e.target.value })} data-testid="video-url-input" className="font-mono" />
              </>
            )}
          </div>
        )}

        {form.creative_format === "carousel" && (
          <div className="mt-3 space-y-2">
            <L hint="2-10 image URLs, each becomes a card">Carousel images</L>
            {form.carousel_images.map((url, i) => (
              <div key={`carousel-${i}-${url || "empty"}`} className="flex items-center gap-2">
                <span className="text-[11px] text-slate-500 font-mono w-8">#{i + 1}</span>
                <Input value={url} onChange={(e) => {
                  const arr = [...form.carousel_images];
                  arr[i] = e.target.value;
                  set({ carousel_images: arr });
                }} data-testid={`carousel-url-${i}`} className="font-mono" placeholder="https://..." />
                {form.carousel_images.length > 2 && (
                  <button onClick={() => set({ carousel_images: form.carousel_images.filter((_, j) => j !== i) })}
                    data-testid={`carousel-remove-${i}`} className="text-slate-400 hover:text-red-600 p-1">
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
            {form.carousel_images.length < 10 && (
              <button onClick={() => set({ carousel_images: [...form.carousel_images, ""] })} data-testid="carousel-add-btn"
                className="text-[11px] px-2 py-1 border border-slate-200 rounded hover:bg-slate-50 flex items-center gap-1">
                <Plus className="w-3 h-3" /> Add card
              </button>
            )}
          </div>
        )}
      </Card>

      <Card title="Destination">
        <div className="space-y-3">
          <div>
            <div className="flex items-center justify-between">
              <L hint="Offer landing page (use Build URL to auto-append macros)">Website URL</L>
              <button type="button" onClick={openUrlBuilder} data-testid="open-url-builder"
                className="text-[11px] text-indigo-600 hover:underline font-mono">Build URL →</button>
            </div>
            <div className="relative">
              <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
              <Input value={form.landing_url} onChange={(e) => set({ landing_url: e.target.value })}
                placeholder="https://offer.example.com/landing?utm_…" data-testid="landing-url-input"
                className="pl-8 font-mono" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <L hint="Short domain shown under headline (optional)">Display link</L>
              <Input value={form.display_link} onChange={(e) => set({ display_link: e.target.value })} data-testid="display-link-input"
                placeholder="example.com" className="font-mono" />
            </div>
            <div>
              <L hint="Appended to every click (URL parameters)">URL tags</L>
              <Input value={form.url_tags} onChange={(e) => set({ url_tags: e.target.value })} data-testid="url-tags-input"
                placeholder="utm_source=fb&utm_medium={{campaign.id}}" className="font-mono" />
            </div>
          </div>
        </div>
      </Card>

      <Card title="Ad creative">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <L>Headline ≤40 chars</L>
            <Input maxLength={40} value={form.headline} onChange={(e) => set({ headline: e.target.value })} data-testid="headline-input" />
          </div>
          <div>
            <L>Description ≤25 chars</L>
            <Input maxLength={25} value={form.description} onChange={(e) => set({ description: e.target.value })} data-testid="description-input" />
          </div>
          <div className="col-span-2">
            <L>Primary text ≤125 chars</L>
            <textarea maxLength={125} rows={2} value={form.primary_text} onChange={(e) => set({ primary_text: e.target.value })}
              data-testid="primary-text-input"
              className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500/40" />
          </div>
          <div>
            <L>Call-to-action</L>
            <Select value={form.call_to_action} onChange={(e) => set({ call_to_action: e.target.value })} data-testid="cta-select">
              {CTAS.map((c) => <option key={c} value={c}>{c.replaceAll("_", " ")}</option>)}
            </Select>
          </div>
        </div>
      </Card>

      <Card title="Enhancements">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-xs">
              <div className="font-medium text-slate-900">Advantage+ creative enhancements</div>
              <div className="text-[11px] text-slate-500">FB auto-optimizes brightness, music, text</div>
            </div>
            <Toggle checked={form.advantage_creative_enhancements} onChange={(v) => set({ advantage_creative_enhancements: v })} testId="advantage-creative-toggle" />
          </div>
          <div className="flex items-center justify-between">
            <div className="text-xs">
              <div className="font-medium text-slate-900">Multi-advertiser ads</div>
              <div className="text-[11px] text-slate-500">Show your ad alongside similar advertisers</div>
            </div>
            <Toggle checked={form.multi_advertiser_ads} onChange={(v) => set({ multi_advertiser_ads: v })} testId="multi-advertiser-toggle" />
          </div>
        </div>
      </Card>

      <VariantPoolCard form={form} set={set} />
    </div>
  );
}

// ─────────────────────────── Variant pool ───────────────────────────
function VariantPoolCard({ form, set }) {
  const cp = form.creative_pool || [];
  const kp = form.copy_pool || [];
  const newId = () => `v_${Math.random().toString(36).slice(2, 10)}`;
  const addCreative = () => {
    if (cp.length >= 10) return;
    set({ creative_pool: [...cp, { _id: newId(), image_url: "", video_url: "" }] });
  };
  const addCopy = () => {
    if (kp.length >= 10) return;
    set({ copy_pool: [...kp, { _id: newId(), headline: "", primary_text: "", description: "" }] });
  };
  const patchCreative = (i, patch) => {
    const arr = cp.map((x, idx) => (idx === i ? { ...x, ...patch } : x));
    set({ creative_pool: arr });
  };
  const patchCopy = (i, patch) => {
    const arr = kp.map((x, idx) => (idx === i ? { ...x, ...patch } : x));
    set({ copy_pool: arr });
  };
  const removeCreative = (i) => set({ creative_pool: cp.filter((_, idx) => idx !== i) });
  const removeCopy = (i) => set({ copy_pool: kp.filter((_, idx) => idx !== i) });

  const cpFilled = cp.filter((c) => c.image_url || c.video_url).length;
  const kpFilled = kp.filter((k) => k.headline).length;
  const count =
    cpFilled === 0 && kpFilled === 0 ? 0
    : form.pairing_mode === "cross" ? Math.min(10, Math.max(1, cpFilled) * Math.max(1, kpFilled))
    : Math.min(10, Math.max(cpFilled, kpFilled));

  return (
    <Card
      title={`Spin variants · ${count === 0 ? "off" : `${count} ads queued`}`}
      hint="Paste multiple creatives + copies → one campaign, N ads, FB picks the winner"
    >
      {count >= 2 && (
        <div className="mb-3 bg-green-50 border border-green-200 rounded px-2 py-1.5 text-[11px] text-green-800 font-mono flex items-center gap-2" data-testid="spin-count-banner">
          <Zap className="w-3 h-3" /> Publish button will create <strong>{count} ads</strong> in 1 campaign → 1 adset
        </div>
      )}
      <div className="space-y-4">
        {/* Creative pool */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <L hint={`${cpFilled}/${cp.length} filled · max 10`}>Creative pool</L>
            <button onClick={addCreative} disabled={cp.length >= 10} data-testid="add-creative-btn"
              className="text-[11px] px-2 py-0.5 border border-slate-200 rounded hover:bg-slate-50 flex items-center gap-1 disabled:opacity-50">
              <Plus className="w-3 h-3" /> Add creative
            </button>
          </div>
          <div className="space-y-1.5">
            {cp.map((c, i) => (
              <div key={c._id || `cp-${i}`} className="flex items-center gap-1.5" data-testid={`creative-row-${i}`}>
                <span className="text-[10px] text-slate-500 font-mono w-6">#{i + 1}</span>
                <Input value={c.image_url || ""} onChange={(e) => patchCreative(i, { image_url: e.target.value, video_url: "" })}
                  placeholder="https://… image URL" data-testid={`creative-img-${i}`} className="font-mono text-[11px]" />
                <span className="text-[10px] text-slate-400">or</span>
                <Input value={c.video_url || ""} onChange={(e) => patchCreative(i, { video_url: e.target.value, image_url: "" })}
                  placeholder="/api/static/videos/…mp4" data-testid={`creative-vid-${i}`} className="font-mono text-[11px]" />
                <button onClick={() => removeCreative(i)} data-testid={`creative-remove-${i}`} className="text-slate-400 hover:text-red-600 p-0.5">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
            {cp.length === 0 && (
              <div className="text-[11px] text-slate-400 italic">Empty — uses the single Ad-step creative as variant 1.</div>
            )}
          </div>
        </div>

        {/* Copy pool */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <L hint={`${kpFilled}/${kp.length} filled · max 10`}>Copy pool</L>
            <button onClick={addCopy} disabled={kp.length >= 10} data-testid="add-copy-btn"
              className="text-[11px] px-2 py-0.5 border border-slate-200 rounded hover:bg-slate-50 flex items-center gap-1 disabled:opacity-50">
              <Plus className="w-3 h-3" /> Add copy
            </button>
          </div>
          <div className="space-y-2">
            {kp.map((k, i) => (
              <div key={k._id || `kp-${i}`} className="border border-slate-200 rounded p-2 space-y-1.5" data-testid={`copy-row-${i}`}>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-slate-500 font-mono w-6">#{i + 1}</span>
                  <Input value={k.headline || ""} maxLength={40} onChange={(e) => patchCopy(i, { headline: e.target.value })}
                    placeholder="Headline ≤40" data-testid={`copy-headline-${i}`} className="flex-1 text-[11px]" />
                  <Input value={k.description || ""} maxLength={25} onChange={(e) => patchCopy(i, { description: e.target.value })}
                    placeholder="Description ≤25" data-testid={`copy-desc-${i}`} className="flex-1 text-[11px]" />
                  <button onClick={() => removeCopy(i)} data-testid={`copy-remove-${i}`} className="text-slate-400 hover:text-red-600 p-0.5">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
                <textarea rows={1} maxLength={125} value={k.primary_text || ""} onChange={(e) => patchCopy(i, { primary_text: e.target.value })}
                  placeholder="Primary text ≤125" data-testid={`copy-primary-${i}`}
                  className="w-full bg-white border border-slate-200 rounded-md px-2 py-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-blue-500/40" />
              </div>
            ))}
            {kp.length === 0 && (
              <div className="text-[11px] text-slate-400 italic">Empty — uses the single Ad-step copy as variant 1.</div>
            )}
          </div>
        </div>

        {/* Pairing mode */}
        <div className="pt-2 border-t border-slate-100">
          <L>Pairing mode</L>
          <div className="flex gap-1.5" data-testid="pairing-mode">
            {[
              { v: "zip", label: "Zip (N)", hint: "Pair 1:1; shorter pool cycles" },
              { v: "cross", label: "Cross (N×M)", hint: "Every creative × every copy (capped at 10)" },
            ].map((o) => {
              const on = form.pairing_mode === o.v;
              return (
                <button key={o.v} onClick={() => set({ pairing_mode: o.v })} data-testid={`pairing-${o.v}`}
                  className={`flex-1 text-[11px] px-2 py-2 rounded border text-left ${on ? "bg-blue-50 border-blue-400" : "bg-white border-slate-200"}`}>
                  <div className="font-medium text-slate-900">{o.label}</div>
                  <div className="text-[10px] text-slate-500">{o.hint}</div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </Card>
  );
}

// ─────────────────────────── Step 4: Review ───────────────────────────
function ReviewStep({ form, stepErrors, onJumpTo }) {
  const rows = [
    ["Campaign", [
      ["Name", form.campaign_name],
      ["Objective", form.objective],
      ["Buying type", form.buying_type],
      ["Budget", `${form.budget_strategy === "campaign" ? "CBO" : "ABO"} · ${form.budget_kind} · $${form.budget}`],
      ["Bid strategy", form.bid_strategy],
      ["Special categories", form.special_ad_categories.join(", ") || "None"],
      ["A/B test", form.ab_test_enabled ? "Enabled" : "Off"],
    ], "campaign"],
    ["Ad Set", [
      ["Name", form.adset_name || `${form.campaign_name} - AdSet`],
      ["Optimization", form.optimization_goal],
      ["Pixel", form.pixel_id || "—"],
      ["Conversion event", form.custom_event_type || "—"],
      ["Attribution", `${form.attribution_click_days}d click · ${form.attribution_view_days}d view`],
      ["Schedule", form.start_time ? `${form.start_time} → ${form.end_time || "ongoing"}` : "Immediate"],
      ["Countries", form.countries.join(", ")],
      ["Age", `${form.age_min}–${form.age_max}`],
      ["Placements", form.placements_auto ? "Advantage+" : form.publisher_platforms.join(", ")],
      ["Advantage+ audience", form.advantage_audience ? "On" : "Off"],
    ], "adset"],
    ["Ad", [
      ["Name", form.ad_name || `${form.campaign_name} - Ad`],
      ["FB Page", form.page_id || "—"],
      ["IG account", form.instagram_actor_id || "(uses FB page)"],
      ["Format", form.creative_format === "carousel" ? `Carousel · ${form.carousel_images.filter(Boolean).length} cards` : `${form.creative_kind}`],
      ["Destination", form.landing_url],
      ["Display link", form.display_link || "—"],
      ["URL tags", form.url_tags || "—"],
      ["Headline", form.headline],
      ["Primary text", form.primary_text],
      ["CTA", form.call_to_action],
      ["Multi-advertiser", form.multi_advertiser_ads ? "On" : "Off"],
      ["Creative enhancements", form.advantage_creative_enhancements ? "On" : "Off"],
      ["Variant pool", `${(form.creative_pool || []).filter((c) => c.image_url || c.video_url).length} creatives × ${(form.copy_pool || []).filter((k) => k.headline).length} copies (${form.pairing_mode})`],
    ], "ad"],
  ];
  return (
    <div className="space-y-4" data-testid="step-review">
      {stepErrors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 space-y-1" data-testid="review-errors">
          <div className="text-xs font-semibold text-red-700">Fix these before publishing:</div>
          {stepErrors.map((e, i) => (
            <button key={`${e.step}-${e.msg}-${i}`} onClick={() => onJumpTo(e.step)} className="block text-[11px] text-red-700 hover:underline text-left">
              → {e.msg} (step: {e.step})
            </button>
          ))}
        </div>
      )}
      {rows.map(([title, kvs, stepId]) => (
        <Card key={title} title={title} onEdit={() => onJumpTo(stepId)}>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
            {kvs.map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="text-slate-500">{k}</dt>
                <dd className="text-slate-900 font-mono truncate" title={String(v)}>{String(v) || "—"}</dd>
              </div>
            ))}
          </dl>
        </Card>
      ))}
    </div>
  );
}

// ─────────────────────────── UI primitives ───────────────────────────
function Card({ title, hint, children, onEdit }) {
  return (
    <section className="border border-slate-200 rounded-md p-4 bg-white">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs font-semibold text-slate-900">{title}</div>
          {hint && <div className="text-[10px] text-slate-400 font-mono">{hint}</div>}
        </div>
        {onEdit && (
          <button onClick={onEdit} className="text-[11px] text-blue-600 hover:underline" data-testid={`edit-${title.toLowerCase().replace(/\s+/g,'-')}`}>Edit</button>
        )}
      </div>
      {children}
    </section>
  );
}

function RadioRow({ label, value, onChange, options, testId }) {
  return (
    <div>
      {label && <L>{label}</L>}
      <div className="space-y-1.5" data-testid={testId}>
        {options.map((o) => (
          <button key={o.value} onClick={() => onChange(o.value)} data-testid={`${testId}-${o.value}`}
            className={`w-full text-left px-3 py-2 rounded border transition-colors ${
              value === o.value ? "bg-blue-50 border-blue-400" : "bg-white border-slate-200 hover:bg-slate-50"
            }`}>
            <div className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-full border-2 ${value === o.value ? "border-blue-600 bg-blue-600" : "border-slate-300"}`}>
                {value === o.value && <span className="block w-1 h-1 bg-white rounded-full m-0.5" />}
              </span>
              <span className="text-xs font-medium text-slate-900">{o.label}</span>
            </div>
            {o.hint && <div className="text-[11px] text-slate-500 mt-0.5 ml-5">{o.hint}</div>}
          </button>
        ))}
      </div>
    </div>
  );
}
